import { useCallback, useRef, useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import logger from "../utils/UnifiedLogger";
import { useAbortController } from "./useAbortController";
import {
  trackApiError,
  trackNetworkError,
  trackTimeoutError
} from "../utils/ErrorTracker";
import { decryptFromBackend, encryptForBackend } from "../utils/utilCrypto";

export const useApi = () => {
  const { t } = useTranslation();
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const requestQueue = useRef([]);
  const activeRequests = useRef(new Map());
  const retryAttempts = useRef(new Map());
  const {
    createApiController,
    abortApiRequest,
    abortAllApiRequests,
    isGlobalAborted,
  } = useAbortController();

  const getTimeoutMessages = useCallback(
    () => [
      {
        time: 2000,
        message: t("botResponseMessage.api.initiating"),
        type: "INITIATING",
      },
      {
        time: 5000,
        message: t("botResponseMessage.api.processingRequest"),
        type: "PROCESSING",
      },
      {
        time: 10000,
        message: t("botResponseMessage.api.stillWorking"),
        type: "WORKING",
      },
      {
        time: 15000,
        message: t("botResponseMessage.api.almostThere"),
        type: "PROGRESS",
      },
      {
        time: 25000,
        message: t("botResponseMessage.api.complexProcessing"),
        type: "DELAY",
      },
      {
        time: 40000,
        message: t("botResponseMessage.api.optimizingResults"),
        type: "OPTIMIZING",
      },
      {
        time: 60000,
        message: t("botResponseMessage.api.finalProcessing"),
        type: "FINALIZING",
      },
      {
        time: 90000,
        message: t("botResponseMessage.api.thankYouForPatience"),
        type: "PATIENCE",
      },
    ],
    [t]
  );

  const calculateRequestPriority = useCallback((config) => {
    const {
      url,
      method,
      priority = "normal",
      // component,
      // params,
      // body,
      // query,
    } = config;
    let score = 50;

    if (method === "GET") score += 10;
    if (method === "POST") score += 20;
    if (method === "PUT" || method === "PATCH") score += 15;
    if (method === "DELETE") score += 25;

    if (url.includes("/auth/")) score += 30;
    if (url.includes("/user/")) score += 20;
    if (url.includes("/search/")) score += 15;
    if (url.includes("/analytics/")) score -= 10;

    const priorityMap = { low: -20, normal: 0, high: 20, critical: 40 };
    score += priorityMap[priority] || 0;

    return Math.max(0, Math.min(100, score));
  }, []);

  const shouldRetry = useCallback((error, attempt, config) => {
    const maxRetries = config.maxRetries || 3;
    if (attempt >= maxRetries) return false;

    if (error.response?.status >= 400 && error.response?.status < 500) {
      return [408, 429, 499].includes(error.response.status);
    }

    return (
      error.code === "ECONNABORTED" ||
      error.code === "NETWORK_ERROR" ||
      error.response?.status >= 500
    );
  }, []);

  const generateCurlCommand = useCallback(
    (config, body, params = {}, query = {}) => {
      try {
        const queryString = new URLSearchParams(query).toString();
        const fullUrl = queryString
          ? `${config.url}?${queryString}`
          : config.url;

        let curl = `curl -X ${config.method.toUpperCase()}`;
        curl += ` '${fullUrl}'`;

        if (config.headers && typeof config.headers === "object") {
          Object.entries(config.headers).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              curl += ` -H '${key}: ${String(value)}'`;
            }
          });
        }

        if (
          params &&
          typeof params === "object" &&
          Object.keys(params).length > 0
        ) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              curl += ` --data-urlencode '${key}=${String(value)}'`;
            }
          });
        }

        if (body && config.method.toUpperCase() !== "GET") {
          if (body instanceof FormData) {
            curl += ` # FormData detected:`;
            try {
              const formEntries = [];
              for (const [key, value] of body.entries()) {
                if (value instanceof File) {
                  formEntries.push(` -F '${key}=@${value.name}'`);
                } else {
                  formEntries.push(` -F '${key}=${String(value)}'`);
                }
              }
              if (formEntries.length > 0) {
                curl += formEntries.join("");
              } else {
                curl += ` -F 'multipart-form-data'`;
              }
            } catch (e) {
              curl += ` -F 'multipart-form-data'`;
            }
          } else if (typeof body === "string") {
            curl += ` -d '${body}'`;
          } else if (typeof body === "object") {
            try {
              const jsonBody = JSON.stringify(body);
              curl += ` -d '${jsonBody}'`;
            } catch (e) {
              curl += ` -d '[Object - JSON.stringify failed]'`;
            }
          } else {
            curl += ` -d '${String(body)}'`;
          }
        }

        curl += ` --compressed --location --max-redirs 5 --retry 3`;
        return curl;
      } catch (error) {
        return `curl # Error generating command: ${error.message}`;
      }
    },
    []
  );

  const getRetryDelay = useCallback((attempt) => {
    const baseDelay = 1000;
    const maxDelay = 30000;
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 1000;
    return Math.min(maxDelay, exponentialDelay + jitter);
  }, []);

  const getErrorCategory = useCallback((error) => {
    const status = error.response?.status;

    if (!status) {
      if (error.code === "ECONNABORTED") return "TIMEOUT";
      if (error.code === "NETWORK_ERROR" || !navigator.onLine) return "NETWORK";
      return "CONNECTION";
    }

    if (status >= 500) return "SERVER_ERROR";
    if (status >= 400 && status < 500) return "CLIENT_ERROR";
    return "UNKNOWN";
  }, []);

  const sendRequest = useCallback(
    async (options) => {
      const {
        method = "GET",
        url,
        headers = {},
        params = {},
        query = {},
        body = {},
        isFormData = false,
        timeout = 300000,
        maxRetries = 3,
        priority = "normal",
        validateStatus = null,
        onStart = () => {},
        onSuccess = () => {},
        onCancel = () => {},
        onError = () => {},
        onComplete = () => {},
        onProgress = () => {},
        // onStream = () => {},
        onRetry = () => {},
        onQueueAdd = () => {},
        abortable = true,
        component = "Unknown",
        isEncrypted = true,
      } = options;
    
      if (isGlobalAborted()) {
        logger.warning("API", "Request aborted due to global abort", {
          url,
          method,
          component,
        });

        return {
          data: null,
          error: new Error("All operations have been aborted"),
          aborted: true,
        };
      }

      const effectiveTimeout = Math.max(timeout, 300000);
      const requestId = `${method}_${url}_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const abortController = abortable ? createApiController(requestId) : null;
      const requestPriority = calculateRequestPriority({
        url,
        method,
        priority,
        component,
        params,
        body,
        query,
      });
      const startTime = performance.now();

      if (!networkStatus) {
        const queuedRequest = {
          config: options,
          requestId,
          timestamp: Date.now(),
        };
        requestQueue.current.push(queuedRequest);
        onQueueAdd(queuedRequest);

        logger.warning("API", "Request queued due to offline status", {
          url,
          method,
          component,
          queueSize: requestQueue.current.length,
        });

        return {
          data: null,
          error: new Error(t("botResponseMessage.api.offlineQueued")),
          queued: true,
        };
      }

      activeRequests.current.set(requestId, {
        startTime,
        url,
        method,
        abortController,
        onCancel,
        component,
        params,
        body,
        query,
        headers,
        timeout: effectiveTimeout,
      });
      console.log(`Request Payload is `,body);
      onStart({ requestId, priority: requestPriority });
      // logger.info("API", "Starting request", {
      //   requestId,
      //   url,
      //   method,
      //   component,
      //   priority: requestPriority,
      // });

      const timeoutMessages = getTimeoutMessages();
      const timeoutIds = [];
      // let currentAttempt = 0;

      const executeRequest = async (attemptNumber = 0) => {
        // currentAttempt = attemptNumber;

        // MOVED OUTSIDE: Declare config and fullUrl outside try block for catch block access
        let config;
        let fullUrl;

        try {
          if (abortController?.signal.aborted) {
            throw new Error("Request aborted");
          }

          // Build fullUrl and config here, but they're accessible in catch
          const queryString = new URLSearchParams(query).toString();
          fullUrl = queryString ? `${url}?${queryString}` : url;

          config = {
            method,
            url: fullUrl,
            headers: {
              "X-Request-ID": requestId,
              "X-Request-Priority": requestPriority,
              ...headers,
            },
            params,
            timeout: effectiveTimeout,
            validateStatus:
              validateStatus || ((status) => status >= 200 && status < 600),
            signal: abortController?.signal,
          };

          if (isFormData && body instanceof FormData) {
            config.data = body;
            config.headers["Content-Type"] = "multipart/form-data";
          } else if (method !== "GET" && body) {
            if (isEncrypted) {              
              const encryptedBody = encryptForBackend(body);
              config.data = encryptedBody;
              config.headers["X-Encrypted"] = "hybrid-rsa-aes";
            } else {
              config.data = body;
            }
            config.headers["Content-Type"] = "application/json";
          }

          timeoutMessages.forEach(({ time, message, type }, idx) => {
            const id = setTimeout(() => {
              if (!abortController?.signal.aborted) {
                onProgress({
                  requestId,
                  elapsedTime: Math.round(performance.now() - startTime),
                  message,
                  step: idx + 1,
                  totalSteps: timeoutMessages.length,
                  type,
                });
              }
            }, time);
            timeoutIds.push(id);
          });

          const response = await axios(config);
          timeoutIds.forEach(clearTimeout);

          // Check if response status indicates an error
          if (response.status >= 400) {
            const serverError = new Error(
              `Request failed with status code ${response.status}`
            );
            serverError.response = response;
            serverError.request = response.request;
            serverError.config = response.config;
            serverError.code =
              response.status >= 500 ? "ERR_SERVER_ERROR" : "ERR_CLIENT_ERROR";
            throw serverError;
          }

          const metrics = {
            requestId,
            duration: Math.round(performance.now() - startTime),
            status: response.status,
            attempts: attemptNumber + 1,
          };

          // logger.info("API", "Request completed successfully", {
          //   requestId,
          //   url,
          //   method,
          //   status: response.status,
          //   duration: metrics.duration,
          //   attempts: metrics.attempts,
          //   component,
          // });

          onProgress({
            requestId,
            elapsedTime: metrics.duration,
            message: t("botResponseMessage.api.requestComplete"),
            step: timeoutMessages.length + 1,
            totalSteps: timeoutMessages.length + 1,
          });

          if (isEncrypted) {
            let data = response.data;
            if (data && data.encrypted_key) {
              data = decryptFromBackend(data);
              response.data = data;
            }
            onSuccess(response.data, metrics);
          }else{
            onSuccess(response.data, metrics);
          }
          activeRequests.current.delete(requestId);
          retryAttempts.current.delete(requestId);

          return { data: response.data, error: null, metrics };
        } catch (err) {
          timeoutIds.forEach(clearTimeout);

          // Now config and fullUrl are accessible here
          // logger.info("API", "Request error encountered", {
          //   requestId,
          //   url: fullUrl || url, // fallback to original url if fullUrl not set
          //   method,
          //   error: err,
          //   attempt: attemptNumber + 1,
          //   maxRetries,
          //   component,
          // });

          const errorCategory = getErrorCategory(err);
          const errorMetrics = {
            requestId,
            duration: Math.round(performance.now() - startTime),
            attempts: attemptNumber + 1,
            status: err.response?.status || 0,
          };

          // Enhanced error tracking data with proper config and fullUrl access
          const errorTrackingData = {
            message: `${errorCategory}: ${err.message}`,
            code: err.code || "UNKNOWN_ERROR",
            status: err.response?.status || 0,
            statusText: err.response?.statusText || "Unknown Status",
            responseData: err.response?.data,
            responseHeaders: err.response?.headers,
            url: fullUrl || url, // Use fullUrl if available, fallback to original url
            method,
            requestId,
            duration: errorMetrics.duration,
            attempts: errorMetrics.attempts,
            timeout: effectiveTimeout,
            curl: generateCurlCommand(
              config || { method, url: fullUrl || url, headers },
              body,
              params,
              query
            ),
            headers: config?.headers || headers,
            requestData: config?.data || body,
            component,
            stack: err.stack,
            maxRetries,
            shouldRetry: shouldRetry(err, attemptNumber, { maxRetries }),
            errorCategory,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            networkStatus,
          };

          // Handle abort
          if (err.name === "AbortError" || err.message === "Request aborted") {
            const abortMetrics = {
              requestId,
              duration: Math.round(performance.now() - startTime),
              attempts: attemptNumber + 1,
              status: 0,
            };

            logger.info("API", "Request aborted", {
              requestId,
              url: fullUrl || url,
              method,
              duration: abortMetrics.duration,
              component,
            });

            onCancel({
              requestId,
              reason: "Request aborted",
              metrics: abortMetrics,
            });

            activeRequests.current.delete(requestId);
            retryAttempts.current.delete(requestId);

            return {
              data: null,
              error: new Error("Request aborted"),
              metrics: abortMetrics,
              aborted: true,
              requestId,
            };
          }

          // Handle retries
          if (shouldRetry(err, attemptNumber, { maxRetries })) {
            const retryDelay = getRetryDelay(attemptNumber);

            // logger.warning("API", "Request failed, will retry", {
            //   requestId,
            //   url: fullUrl || url,
            //   method,
            //   attempt: attemptNumber + 1,
            //   maxRetries,
            //   retryDelay,
            //   error: err.message,
            //   status: err.response?.status,
            //   component,
            // });

            // Track retry attempt
            await trackApiError({
              ...errorTrackingData,
              message: `${errorCategory}: Retrying (attempt ${
                attemptNumber + 1
              }/${maxRetries}) - ${err.message}`,
              retryDelay,
              isRetryAttempt: true,
            });

            onRetry({
              error: err,
              attempt: attemptNumber + 1,
              maxRetries,
              retryDelay,
              requestId,
            });

            return new Promise((resolve) => {
              const retryTimeout = setTimeout(async () => {
                if (abortController?.signal.aborted) {
                  resolve({
                    data: null,
                    error: new Error("Request aborted during retry"),
                    aborted: true,
                    requestId,
                  });
                } else {
                  resolve(await executeRequest(attemptNumber + 1));
                }
              }, retryDelay);

              if (abortController?.signal) {
                abortController.signal.addEventListener("abort", () => {
                  clearTimeout(retryTimeout);
                  resolve({
                    data: null,
                    error: new Error("Request aborted during retry"),
                    aborted: true,
                    requestId,
                  });
                });
              }
            });
          }

          // Final failure - track based on error type
          if (err.code === "ECONNABORTED") {
            await trackTimeoutError({
              url: fullUrl || url,
              method,
              requestId,
              duration: errorMetrics.duration,
              attempts: errorMetrics.attempts,
              timeout: effectiveTimeout,
              curl: generateCurlCommand(
                config || { method, url: fullUrl || url, headers },
                body,
                params,
                query
              ),
              headers: config?.headers || headers,
              requestData: config?.data || body,
              component,
            });
          } else if (errorCategory === "SERVER_ERROR") {
            // Enhanced server error tracking (5xx errors)
            await trackApiError({
              ...errorTrackingData,
              message: `SERVER ERROR (${err.response?.status}): ${err.message}`,
              serverErrorType:
                err.response?.status === 500
                  ? "INTERNAL_SERVER_ERROR"
                  : err.response?.status === 502
                  ? "BAD_GATEWAY"
                  : err.response?.status === 503
                  ? "SERVICE_UNAVAILABLE"
                  : err.response?.status === 504
                  ? "GATEWAY_TIMEOUT"
                  : "OTHER_SERVER_ERROR",
              isFinalFailure: true,
            });
          } else if (errorCategory === "CLIENT_ERROR") {
            // Track client errors (4xx errors)
            await trackApiError({
              ...errorTrackingData,
              message: `CLIENT ERROR (${err.response?.status}): ${err.message}`,
              clientErrorType:
                err.response?.status === 400
                  ? "BAD_REQUEST"
                  : err.response?.status === 401
                  ? "UNAUTHORIZED"
                  : err.response?.status === 403
                  ? "FORBIDDEN"
                  : err.response?.status === 404
                  ? "NOT_FOUND"
                  : err.response?.status === 429
                  ? "TOO_MANY_REQUESTS"
                  : "OTHER_CLIENT_ERROR",
              isFinalFailure: true,
            });
          } else {
            // Track network/connection errors
            await trackNetworkError(err, {
              url: fullUrl || url,
              method,
              requestId,
              duration: errorMetrics.duration,
              attempts: errorMetrics.attempts,
              timeout: effectiveTimeout,
              curl: generateCurlCommand(
                config || { method, url: fullUrl || url, headers },
                body,
                params,
                query
              ),
              headers: config?.headers || headers,
              requestData: config?.data || body,
              component,
              maxRetries,
            });
          }

          const errorMessage =
            err.code === "ECONNABORTED"
              ? t("botResponseMessage.api.requestTimeout")
              : errorCategory === "SERVER_ERROR"
              ? t("botResponseMessage.api.serverError")
              : t("botResponseMessage.api.requestError");

          // logger.error("API", "Request failed permanently", {
          //   requestId,
          //   url: fullUrl || url,
          //   method,
          //   status: errorMetrics.status,
          //   duration: errorMetrics.duration,
          //   attempts: errorMetrics.attempts,
          //   error: err.message,
          //   errorCategory,
          //   component,
          // });

          onProgress({
            requestId,
            elapsedTime: errorMetrics.duration,
            message: errorMessage,
          });

          onError(err, errorMetrics);
          activeRequests.current.delete(requestId);
          retryAttempts.current.delete(requestId);

          return { data: null, error: err, metrics: errorMetrics };
        }
      };

      try {
        return await executeRequest(0);
      } finally {
        timeoutIds.forEach(clearTimeout);
        onComplete({ requestId });
      }
    },
    [
      t,
      networkStatus,
      shouldRetry,
      getRetryDelay,
      calculateRequestPriority,
      getTimeoutMessages,
      createApiController,
      isGlobalAborted,
      generateCurlCommand,
      getErrorCategory,
    ]
  );

  // Rest of your methods remain unchanged...
  const cancelRequest = useCallback(
    (requestId) => {
      const request = activeRequests.current.get(requestId);
      if (request) {
        logger.info("API", "Cancelling individual request", {
          requestId,
          url: request.url,
          method: request.method,
          component: request.component,
          duration: Math.round(performance.now() - request.startTime),
        });

        if (request.onCancel) {
          request.onCancel({
            requestId,
            reason: "Request cancelled manually",
            url: request.url,
            method: request.method,
          });
        }
      }

      return abortApiRequest(requestId);
    },
    [abortApiRequest]
  );

  const cancelAllRequests = useCallback(() => {
    try {
      const activeCount = activeRequests.current.size;
      const queuedCount = requestQueue.current.length;

      logger.info("API", "Cancelling all requests", {
        activeCount,
        queuedCount,
        totalCancelled: activeCount + queuedCount,
      });

      const activeRequestsDetails = Array.from(
        activeRequests.current.entries()
      ).map(([requestId, req]) => ({
        requestId,
        url: req.url,
        method: req.method,
        component: req.component,
        duration: Math.round(performance.now() - req.startTime),
        timeout: req.timeout,
        curl: generateCurlCommand(
          {
            method: req.method,
            url: req.url,
            headers: req.headers,
          },
          req.body,
          req.params,
          req.query
        ),
      }));

      const queuedRequestsDetails = requestQueue.current.map((req) => ({
        url: req.config.url,
        method: req.config.method,
        component: req.config.component,
        queuedAt: req.timestamp,
        queueDuration: Date.now() - req.timestamp,
        curl: generateCurlCommand(
          {
            method: req.config.method,
            url: req.config.url,
            headers: req.config.headers,
          },
          req.config.body,
          req.config.params,
          req.config.query
        ),
      }));

      logger.info("API", "Active requests details", {
        requests: activeRequestsDetails,
      });

      trackApiError({
        message: "All API requests cancelled",
        code: "ALL_REQUESTS_CANCELLED",
        status: 0,
        url:
          activeCount > 0
            ? activeRequestsDetails.map((req) => req.url).join(", ")
            : null,
        method:
          activeCount > 0
            ? activeRequestsDetails.map((req) => req.method).join(", ")
            : null,
        requestId:
          Array.from(activeRequests.current.keys()).join(", ") || "BULK_CANCEL",
        duration:
          activeCount > 0
            ? Math.max(...activeRequestsDetails.map((req) => req.duration))
            : 0,
        attempts: 0,
        timeout:
          activeCount > 0
            ? Math.max(...activeRequestsDetails.map((req) => req.timeout || 0))
            : 0,
        curl:
          activeCount > 0
            ? `# Active requests cancelled (${activeCount}):\n${activeRequestsDetails
                .map((req) => req.curl)
                .join("\n")}\n` +
              (queuedCount > 0
                ? `# Queued requests cancelled (${queuedCount}):\n${queuedRequestsDetails
                    .map((req) => req.curl)
                    .join("\n")}`
                : "")
            : "# No active or queued requests to cancel",
        headers: null,
        requestData: null,
        component: "useApi - Global Cancel",
        stack: new Error().stack,
        maxRetries: 0,
        shouldRetry: false,
        bulkOperation: true,
        activeRequestsCount: activeCount,
        queuedRequestsCount: queuedCount,
        totalAffectedRequests: activeCount + queuedCount,
        cancelledRequests: activeRequestsDetails,
        queuedRequests: queuedRequestsDetails,
        networkStatus,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        performanceMetrics: {
          memoryUsage: performance.memory
            ? {
                usedJSHeapSize: performance.memory.usedJSHeapSize,
                totalJSHeapSize: performance.memory.totalJSHeapSize,
                jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
              }
            : null,
          connectionType: navigator.connection
            ? navigator.connection.effectiveType
            : "unknown",
        },
      });

      const requestsToCancel = Array.from(activeRequests.current.entries());
      requestsToCancel.forEach(([requestId, requestInfo]) => {
        if (requestInfo.onCancel) {
          requestInfo.onCancel({
            requestId,
            reason: "All requests cancelled",
            url: requestInfo.url,
            method: requestInfo.method,
          });
        }
      });

      const abortedCount = abortAllApiRequests();
      activeRequests.current.clear();
      retryAttempts.current.clear();
      requestQueue.current = [];

      logger.info("API", "All requests cancelled successfully", {
        abortedCount,
      });

      return abortedCount;
    } catch (error) {
      logger.error("API", "Error cancelling all requests", {
        error: error.message,
        activeRequests: activeRequests.current.size,
        queuedRequests: requestQueue.current.length,
      });

      activeRequests.current.clear();
      retryAttempts.current.clear();
      requestQueue.current = [];

      return 0;
    }
  }, [abortAllApiRequests, generateCurlCommand, networkStatus]);

  const processQueuedRequests = useCallback(async () => {
    const queue = [...requestQueue.current];
    requestQueue.current = [];

    if (queue.length > 0) {
      logger.info("API", "Processing queued requests", {
        queueSize: queue.length,
      });
    }

    let successCount = 0;
    let errorCount = 0;

    for (const queuedRequest of queue) {
      try {
        await sendRequest(queuedRequest.config);
        successCount++;
        logger.debug("API", "Queued request processed successfully", {
          url: queuedRequest.config.url,
          method: queuedRequest.config.method,
        });
      } catch (error) {
        errorCount++;
        logger.error("API", "Failed to process queued request", {
          url: queuedRequest.config.url,
          method: queuedRequest.config.method,
          error: error.message,
          queuedAt: queuedRequest.timestamp,
          processedAt: Date.now(),
        });
      }
    }

    if (queue.length > 0) {
      logger.info("API", "Queue processing completed", {
        totalProcessed: queue.length,
        successCount,
        errorCount,
        successRate: (successCount / queue.length) * 100,
      });
    }
  }, [sendRequest]);

  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus(true);
      logger.info(
        "NETWORK",
        "Connection restored - processing queued requests",
        {
          queuedRequestsCount: requestQueue.current.length,
          activeRequestsCount: activeRequests.current.size,
        }
      );
      processQueuedRequests();
    };

    const handleOffline = () => {
      setNetworkStatus(false);
      logger.warning("NETWORK", "Connection lost - queuing future requests", {
        activeRequestsCount: activeRequests.current.size,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [processQueuedRequests]);

  return {
    sendRequest,
    cancelRequest,
    cancelAllRequests,
    getActiveRequestsCount: () => activeRequests.current.size,
    getQueuedRequestsCount: () => requestQueue.current.length,
    getApiMetrics: () => ({
      activeRequests: activeRequests.current.size,
      queuedRequests: requestQueue.current.length,
      networkStatus,
    }),
    networkStatus,
    processQueuedRequests,
  };
};
